import { useState, useEffect, useRef, useCallback, useMemo, } from "react";
import { Button, TextField, MenuItem, Select, InputLabel, FormControl, FormHelperText, Box, Typography, Divider, Grid, Autocomplete } from "@mui/material";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { GoogleMap, LoadScript, StandaloneSearchBox, Marker } from '@react-google-maps/api';
import { useDarkMode } from '../components/DarkModeContext';
import colors from "../colour";
import { useNavigate, useLocation } from "react-router-dom";
import InputAdornment from '@mui/material/InputAdornment';
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import { Visibility, VisibilityOff } from "@mui/icons-material";
// API
import { updateSite, registerSite } from "../api/plant"; // Add updateSite function
import { getPlantList } from "../api/plant";



const AddSite = () => {
    // --- Router & edit-mode setup ---
    const location = useLocation();
    const editingUser = location.state?.editingUser;
    const isEdit = Boolean(editingUser);
    const existingUser = isEdit; // alias for JSX
    const [editId, initData] = useMemo(
        () => editingUser || [null, {}],
        [editingUser]
    );
    const navigate = useNavigate();

    // --- Map constants (memoized) ---
    const mapLibraries = useMemo(() => ['places'], []);
    const containerStyle = useMemo(() => ({ width: '100%', height: '400px' }), []);
    const defaultCenter = useMemo(
        () => ({ lat: 3.15749481870907, lng: 101.7115622103232 }),
        []
    );

    // --- Form state ---
    const [formData, setFormData] = useState({
        username: '',
        user_pwd: '',
        confirm_pwd: "",
        email: '',
        company_name: '',
        company_address: '',
        phone_number: '',
        user_role: '',
        access_level: '1',
        battery_capacity_size: '',
        battery_inverter_size: '',
        solar_system_size: '',
        solar_inverter_size: '',
        tariff: '',
        image: null,
        logo: null,
        longitude: '',
        latitude: '',
        number_of_meters: '',
        p90_estimate: '',
        psh_estimate: '',
        p50_estimate: '',
        solar_inverter_brand: '',
        solar_panel_brand: '',
        solar_install_pic: '',
        battery_c_rating: '',
        battery_dc_brand: '',
        battery_inverter_brand: '',
        battery_install_pic: '',
        battery_install_date: '',
        solar_install_date: '',
        solar_notes: '',
        battery_notes: '',
        pvsyst_report: '',
        smartmeter_install_date: '',
        license_due_date: '',
        license_start_date: '',
        user_type: '',
        clients_owned: [],
        is_ppa: '',
        ppa_owned_by: null,
        energy_supplier: '',

    });
    const [meters, setMeters] = useState([]);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [selectedLogo, setSelectedLogo] = useState(null);
    const { darkMode } = useDarkMode();
    const [existingImage, setExistingImage] = useState(null);
    const [existingLogo, setExistingLogo] = useState(null);
    const [meterErrors, setMeterErrors] = useState([]);
    // const [pshError, setPshError] = useState('');
    const [selectedPDF, setSelectedPDF] = useState(null);
    const [existingPDF, setExistingPDF] = useState(null); // URL or blob string
    const [clientList, setClientList] = useState([]);



    // --- Map & Autocomplete ---
    const searchBoxRef = useRef(null);
    const [mapCenter, setMapCenter] = useState(defaultCenter);
    const [searchTerm, setSearchTerm] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const togglePasswordVisibility = () => {
        setShowPassword((prev) => !prev);
    };

    // --- Pre-fill form in edit mode ---
    useEffect(() => {
        if (!isEdit || !initData) return;


        // 1) build the meters array exactly once
        const rawMeters = Array.isArray(initData.smartmeters) ? initData.smartmeters : [];
        const mappedMeters = rawMeters.map(sm => ({
            client_smartmeter_id: sm.client_smartmeter_id,
            client: sm.client,
            smartmeter_id: sm.smartmeter_id,
            usage: sm.usage ?? '',
            description: sm.description ?? '',
            sm_installation_date: sm.sm_installation_date ?? '',
            serial_number: sm.smartmeter?.serial_number ?? '',
            brand: sm.smartmeter?.brand ?? '',
            model: sm.smartmeter?.model ?? '',
        }));
        setMeters(mappedMeters);

        // 2) pick only the top‐level fields your form actually uses
        setFormData(prev => ({
            ...prev,
            company_name: initData.company_name || '',
            company_address: initData.company_address || '',
            email: initData.email || '',
            phone_number: initData.phone_number || '',
            solar_system_size: initData.solar_system_size || '',
            solar_inverter_size: initData.solar_inverter_size || '',
            battery_capacity_size: initData.battery_capacity_size || '',
            battery_inverter_size: initData.battery_inverter_size || '',
            tariff: initData.tariff || '',
            latitude: initData.latitude || '',
            longitude: initData.longitude || '',
            number_of_meters: mappedMeters.length > 0
                ? String(mappedMeters.length)
                : '',
            p90_estimate: initData.p90_estimate || '',
            p50_estimate: initData.p50_estimate || '',
            solar_inverter_brand: initData.solar_inverter_brand || '',
            solar_panel_brand: initData.solar_panel_brand || '',
            solar_install_pic: initData.solar_install_pic || '',
            solar_install_date: initData.solar_install_date || '',
            psh_estimate: initData.psh_estimate || '',
            solar_notes: initData.solar_notes || '',
            battery_c_rating: initData.battery_c_rating || '',
            battery_dc_brand: initData.battery_dc_brand || '',
            battery_inverter_brand: initData.battery_inverter_brand || '',
            battery_install_pic: initData.battery_install_pic || '',
            battery_install_date: initData.battery_install_date || '',
            battery_notes: initData.battery_notes || '',
            smartmeter_install_date: initData.smartmeter_install_date || '',
            license_due_date: initData.license_due_date || '',
            license_start_date: initData.license_start_date || '',
            user_type: initData.user_type ?? '',
            clients_owned: initData.clients_owned?.map(Number) || [],
            is_ppa: initData.is_ppa ?? '',
            ppa_owned_by: initData.ppa_owned_by ?? '',
            energy_supplier: initData.energy_supplier ?? '',

        }));

        // 3) other one‐offs
        if (initData.company_address) setSearchTerm(initData.company_address);

        if (
            initData.image &&
            initData.image !== "data:image/jpeg;base64,null" &&
            initData.image !== "null"
        ) {
            const raw = initData.image;
            const url = raw.startsWith('data:')
                ? raw
                : `data:image/jpeg;base64,${raw}`;
            setExistingImage(url);
        }

        if (
            initData.logo &&
            initData.logo !== "data:image/jpeg;base64,null" &&
            initData.logo !== "null"
        ) {
            const raw = initData.logo;
            const url = raw.startsWith('data:')
                ? raw
                : `data:image/jpeg;base64,${raw}`;
            setExistingLogo(url);
        }


        if (initData.pvsyst_report) {
            const raw = initData.pvsyst_report;

            // Remove the prefix if needed
            const base64 = raw.startsWith('data:')
                ? raw.split(',')[1]
                : raw;

            const blobURL = base64ToBlobURL(base64);
            setExistingPDF(blobURL);
        }




    }, [isEdit, initData]);

    // --- Validation rules (memoized) ---
    const validationRules = useMemo(() => ({
        email: { regex: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email.' },
        company_name: { check: v => v.trim(), message: 'Company is required.' },
        company_address: { check: v => v.trim(), message: 'Address is required.' },
        phone_number: { regex: /^\d{10,15}$/, message: 'Phone must be 10–15 digits.' },
        battery_capacity_size: { regex: /^(\d+(\.\d+)?|\s*)$/, message: 'Battery Capacity Size must be number.' },
        battery_inverter_size: { regex: /^(\d+(\.\d+)?|\s*)$/, message: 'Battery Inverter Size must be number.' },
        solar_system_size: { regex: /^(\d+(\.\d+)?|\s*)$/, message: 'Solar System Size must be number.' },
        solar_inverter_size: { regex: /^(\d+(\.\d+)?|\s*)$/, message: 'Solar Inverter Size must be number.' },
        tariff: { check: v => !!v, message: 'Tariff is required.' },
        p90_estimate: { regex: /^(\d+(\.\d+)?|\s*)$/, message: 'P90 must be number.' },
        p50_estimate: { regex: /^(\d+(\.\d+)?|\s*)$/, message: 'P50 must be number.' },
        battery_c_rating: { regex: /^(\d+(\.\d+)?|\s*)$/, message: 'Battery C Rating must be number.' },

    }), []);


    // --- Handlers (useCallback) ---
    const handleInputChange = useCallback((e) => {
        const { name, value } = e.target;
        if (name === 'user_role') {
            const access = value === 'user' ? 1 : value === 'sitemanager' ? 2 : '';
            setFormData(fd => ({ ...fd, user_role: value, access_level: access }));
        } else {
            setFormData(fd => ({ ...fd, [name]: value }));
        }
    }, []);

    const handleImageChange = useCallback((e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 2e6) return toast.error('Max 2MB image');
        if (!['image/png', 'image/jpg'].includes(file.type)) return toast.error('Only JPEG');
        setSelectedImage(file);
    }, []);

    const handleLogoChange = useCallback((e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 2e6) return toast.error('Max 2MB image');
        if (!['image/png', 'image/jpg'].includes(file.type)) return toast.error('Only JPEG');
        setSelectedLogo(file);
    }, []);

    const addMeter = () => {
        setMeters(prev => [
            ...prev,
            { serial_number: "", brand: "", model: "", usage: "", description: "", sm_installation_date: "" }
        ]);
    };

    const removeMeter = (index) => {
        setMeters(prev => {
            const updatedMeters = prev.filter((_, idx) => idx !== index);
            return updatedMeters;
        });
    };



    const updateMeterField = useCallback((idx, field, val) => {
        setMeters(ms => ms.map((m, i) => i === idx ? { ...m, [field]: val } : m));
    }, []);

    const handlePlacesChanged = useCallback(async () => {
        const places = searchBoxRef.current?.getPlaces();
        if (!places || !places.length) {
            toast.error("No places found.");
            return;
        }

        const p = places[0];

        if (!p?.geometry?.location) {
            toast.error("Location details not available");
            return;
        }
        const addr = p.formatted_address;
        const lat = p.geometry.location.lat();
        const lng = p.geometry.location.lng();

        setFormData(fd => ({ ...fd, company_address: addr, latitude: lat, longitude: lng }));
        setMapCenter({ lat, lng });
        setSearchTerm(addr);
    }, []);

    //Live confirm password check
    useEffect(() => {
        if (!isEdit && formData.confirm_pwd) {
            if (formData.user_pwd !== formData.confirm_pwd) {
                setErrors(prev => ({ ...prev, confirm_pwd: "Passwords do not match" }));
            } else {
                setErrors(prev => {
                    const { confirm_pwd, ...rest } = prev; // remove confirm_pwd error
                    return rest;
                });
            }
        }
    }, [formData.user_pwd, formData.confirm_pwd, isEdit]);


    // Keep the existing validateForm for full validation on submit
    const validateForm = useCallback(() => {
        const errs = {};

        Object.entries(validationRules).forEach(([field, { regex, check, message }]) => {
            const val = formData[field] + '';
            if (regex ? !regex.test(val) : !(check(val, formData))) {
                errs[field] = message;
            }
        });

        if (!isEdit) {
            if (!/^[a-zA-Z0-9]+$/.test(formData.username)) {
                errs.username = 'Alphanumeric only';
            }
            if (!/(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*?&#]).{8,}/.test(formData.user_pwd)) {
                errs.user_pwd = 'Min 8 chars, letters+numbers+symbol';
            }
            if (!formData.user_role) {
                errs.user_role = 'Role required';
            }
            if (formData.user_pwd !== formData.confirm_pwd) {
                errs.confirm_pwd = 'Passwords do not match';
            }

        }

        // --- Meter validation ---
        const meterErrs = meters.map((m) => {
            return {
                serial_number: !m.serial_number?.trim(),
                brand: !m.brand?.trim(),
                model: !m.model?.trim(),
                usage: !m.usage?.trim(),
                description: !m.description?.trim(),
                sm_installation_date: !m.sm_installation_date?.trim(),
            };
        });
        setMeterErrors(meterErrs);

        if (meters.length !== 0) {
            meterErrs.forEach((err, idx) => {
                Object.entries(err).forEach(([field, hasError]) => {
                    if (hasError) toast.error(`Meter #${idx + 1}: ${field.replace('_', ' ')} is required.`);
                });
            });
        }

        // --- Set & show errors ---
        setErrors(errs);
        Object.values(errs).forEach(msg => toast.error(msg));

        return Object.keys(errs).length === 0 && meters.every(m =>
            meterErrs.every(err => Object.values(err).every(ok => !ok))
        );
    }, [formData, meters, isEdit, validationRules]);


    const handleFormSubmit = useCallback(async () => {
        if (!validateForm()) return;
        setLoading(true);

        try {
            const fd = new FormData();

            // 1) append flat form data
            Object.entries(formData).forEach(([k, v]) => {
                if (v !== null && v !== undefined) {
                    if (Array.isArray(v)) {
                        fd.append(k, JSON.stringify(v));
                    } else {
                        fd.append(k, v);
                    }
                }
            });

            // 2) flag presence of smart meters
            fd.append('has_smart_meter', meters.length > 0);

            // 3) build smartmeter payload correctly
            if (meters.length) {
                let payload;

                if (isEdit) {
                    const originals = Array.isArray(initData.smartmeters) ? initData.smartmeters : [];

                    // ✅ match by unique ID, not by index
                    payload = meters.map((m) => {
                        const orig = originals.find(
                            o =>
                                o.client_smartmeter_id === m.client_smartmeter_id ||
                                o.smartmeter_id === m.smartmeter_id
                        ) || {};

                        return {
                            client_smartmeter_id: orig.client_smartmeter_id ?? null,
                            client: orig.client ?? null,
                            smartmeter_id: orig.smartmeter_id ?? null,
                            usage: m.usage,
                            description: m.description,
                            sm_installation_date: m.sm_installation_date,
                            smartmeter: {
                                serial_number: m.serial_number,
                                brand: m.brand,
                                model: m.model,
                            }
                        };
                    });
                } else {
                    // For new site: no original references
                    payload = meters.map(m => ({
                        usage: m.usage,
                        description: m.description,
                        sm_installation_date: m.sm_installation_date,
                        smartmeter: {
                            serial_number: m.serial_number,
                            brand: m.brand,
                            model: m.model,
                        }
                    }));
                }
                fd.append('smartmeters', JSON.stringify(payload));
            }

            // 4) add image if selected
            if (selectedImage) {
                fd.append('image', selectedImage);
            }
            if (selectedLogo) {
                fd.append('logo', selectedLogo);
            }

            // 5) debug form data
            for (let [key, val] of fd.entries()) {
                console.log("📦", key, val);
            }

            // 6) call correct API
            if (isEdit) {
                await updateSite(editId, fd);
                toast.success('Updated Successful!');
            } else {
                await registerSite(fd);
                toast.success('Added Successful!');
            }

            navigate('/PlantList');
        } catch (e) {
            console.error(e);
            toast.error('Submit failed');
        } finally {
            setLoading(false);
        }
    }, [formData, meters, selectedImage, isEdit, initData, editId, navigate, validateForm, selectedLogo]);

    // --- Static brand-model map ---
    const brandModelMap = useMemo(() => ({ Acrel: ['ADW300', 'ADW400'], Amsolar: ['Test1', 'Test2'] }), []);

    //peak sun hour
    useEffect(() => {
        const { p90_estimate, solar_system_size, psh_estimate } = formData;

        const p90Value = parseFloat(p90_estimate);
        const sizeValue = parseFloat(solar_system_size);

        let newPSH = '';

        if (!isNaN(p90Value) && !isNaN(sizeValue) && sizeValue !== 0) {
            newPSH = (p90Value * 1000 / 365 / sizeValue).toFixed(2);
        }

        // ✅ only update if changed
        if (psh_estimate !== newPSH) {
            setFormData(prev => ({
                ...prev,
                psh_estimate: newPSH,
            }));
        }

    }, [formData.p90_estimate, formData.solar_system_size]);

    const handlePDFChange = useCallback((e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Limit size to e.g. 5MB
        if (file.size > 5e6) return toast.error('Max 5MB PDF');

        // Only allow PDF MIME type
        if (file.type !== 'application/pdf') return toast.error('Only PDF files are allowed');

        setSelectedPDF(file);

        // Optional: Convert to base64 if needed
        const reader = new FileReader();
        reader.onloadend = () => {
            const base64String = reader.result.split(',')[1];
            setFormData(prev => ({
                ...prev,
                pvsyst_report: base64String,
            }));
        };
        reader.readAsDataURL(file);
    }, []);

    const base64ToBlobURL = (base64) => {
        const byteCharacters = atob(base64);
        const byteNumbers = Array.from(byteCharacters, char => char.charCodeAt(0));
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        return URL.createObjectURL(blob);
    };



    // Fetch Client List
    useEffect(() => {
        const fetchPlantList = async () => {
            try {
                const plantList = await getPlantList();
                if (Array.isArray(plantList.data)) {
                    setClientList(plantList.data);
                }
            } catch (error) {
                console.error("Error fetching client list:", error);
            }
        };
        fetchPlantList();
    }, []);




    return (
        <Box
            sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                paddingBottom: "2%"
            }}
        >
            <ToastContainer />
            {/*Header*/}
            <Box
                className={darkMode ? "header_darkM" : "header_lightM"}
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    p: 2,
                    borderBottom: `1px solid ${darkMode ? colors.d3 : colors.l3}`,
                    backgroundColor: darkMode ? colors.d1 : colors.l1,
                    width: '100%',
                    alignItems: "center", // Center vertically
                    height: "10%",


                }}>
                <Box sx={{
                    width: { xs: "50%", md: "30%" },
                    display: "flex",
                    justifyContent: "flex-start", // Center horizontally
                    alignItems: "center", // Center vertically
                    height: "100%", // Ensure height for vertical centering
                    marginLeft: '2rem'
                }}>
                    <Typography
                        variant="h4"
                        sx={{
                            fontFamily: "Montserrat",
                            fontWeight: "bold",
                            fontSize: { xs: '25px', md: '40px' },
                            color: darkMode ? colors.f2 : colors.f3,
                        }}>
                        {existingUser ? "Modify User" : "Add New User"}
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ width: "95%", height: "100%", borderRadius: "1%", border: "0.1vw solid", borderColor: colors.b2, mt: "2.5%", backgroundColor: darkMode ? colors.l5 : colors.f2, padding: "2%", boxShadow: darkMode ? "6px 6px 10px rgba(255, 255, 255, 0.3)" : "6px 6px 10px rgba(0, 0, 0, 0.3)", }}>
                {/*Company Details*/}
                <Box sx={{ display: "flex", width: "100%", height: "100%" }}><Typography sx={{ fontSize: "1.3vw", fontWeight: "bold", pl: "2%", mt: "2%", color: darkMode ? colors.f2 : colors.f3, }}>Company Details</Typography></Box>
                <Box sx={{ width: "100%", height: "100%", padding: "1%", display: "flex", flexDirection: "row" }}>
                    <Grid container sx={{ rowGap: "1.5vh", columnGap: "7%", width: "35%" }}>
                        {/* Username */}
                        <Grid item xs={12} sm={10}>
                            <TextField
                                label="Username*"
                                name="username"
                                margin="dense"
                                fullWidth
                                value={formData.username}
                                onChange={handleInputChange}
                                error={!!errors.username}
                                autoComplete="new-username"
                                disabled={existingUser}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3, // Border color based on darkMode
                                        },
                                        '&:hover fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3, // Border color on hover
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3, // Border color when focused
                                        },
                                    },
                                }}
                                InputLabelProps={{ sx: { color: darkMode ? colors.f4 : colors.f4 } }}
                                inputProps={{ style: { color: darkMode ? colors.f2 : colors.f3 } }}
                            />
                            {errors.username && <FormHelperText error sx={{ color: darkMode ? colors.f2 : colors.f3 }}>{errors.username}</FormHelperText>}
                        </Grid>
                        {/* Password */}
                        <Grid item xs={12} sm={10}>
                            <TextField
                                label="Password*"
                                name="user_pwd"
                                type={showPassword ? "text" : "password"}
                                margin="dense"
                                fullWidth
                                value={formData.user_pwd}
                                onChange={handleInputChange}
                                error={!!errors.user_pwd}
                                autoComplete="new-password"
                                disabled={existingUser}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        "& fieldset": {
                                            borderColor: darkMode ? colors.f2 : colors.f3
                                        },
                                        "&:hover fieldset": {
                                            borderColor: darkMode ? colors.f2 : colors.f3
                                        },
                                        "&.Mui-focused fieldset": {
                                            borderColor: darkMode ? colors.f2 : colors.f3
                                        }
                                    }
                                }}
                                InputLabelProps={{
                                    sx: { color: darkMode ? colors.f4 : colors.f4 }
                                }}
                                inputProps={{
                                    style: { color: darkMode ? colors.f2 : colors.f3 }
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={togglePasswordVisibility}
                                                edge="end"
                                                sx={{ color: colors.f1 }}
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                            {errors.user_pwd && (
                                <FormHelperText
                                    error
                                    sx={{ color: darkMode ? colors.f2 : colors.f3 }}
                                >
                                    {errors.user_pwd}
                                </FormHelperText>
                            )}
                        </Grid>
                        {/* Confirm Password */}
                        <Grid item xs={12} sm={10}>
                            <TextField
                                label="Confirm Password*"
                                name="confirm_pwd"
                                type={showPassword ? "text" : "password"} // reuse toggle
                                margin="dense"
                                fullWidth
                                value={formData.confirm_pwd}
                                onChange={handleInputChange}
                                error={!!errors.confirm_pwd}
                                autoComplete="new-password"
                                disabled={existingUser}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        "& fieldset": {
                                            borderColor: darkMode ? colors.f2 : colors.f3
                                        },
                                        "&:hover fieldset": {
                                            borderColor: darkMode ? colors.f2 : colors.f3
                                        },
                                        "&.Mui-focused fieldset": {
                                            borderColor: darkMode ? colors.f2 : colors.f3
                                        }
                                    }
                                }}
                                InputLabelProps={{
                                    sx: { color: darkMode ? colors.f4 : colors.f4 }
                                }}
                                inputProps={{
                                    style: { color: darkMode ? colors.f2 : colors.f3 }
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={togglePasswordVisibility}
                                                edge="end"
                                                sx={{ color: colors.f1 }}
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                            {errors.confirm_pwd && (
                                <FormHelperText
                                    error
                                    sx={{ color: darkMode ? colors.f2 : colors.f3 }}
                                >
                                    {errors.confirm_pwd}
                                </FormHelperText>
                            )}
                        </Grid>
                        {/* Email */}
                        <Grid item xs={12} sm={10}>
                            <TextField
                                label="Email*"
                                name="email"
                                fullWidth
                                margin="dense"
                                value={formData.email}
                                onChange={handleInputChange}

                                error={!!errors.email}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3, // Border color based on darkMode
                                        },
                                        '&:hover fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3, // Border color on hover
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3, // Border color when focused
                                        },
                                    },
                                }}
                                InputLabelProps={{ sx: { color: darkMode ? colors.f4 : colors.f4 } }}
                                inputProps={{ style: { color: darkMode ? colors.f2 : colors.f3 } }}
                            />
                            {errors.email && <FormHelperText error sx={{ color: darkMode ? colors.f2 : colors.f3 }}>{errors.email}</FormHelperText>}
                        </Grid>
                        {/* Company Name */}
                        <Grid item xs={12} sm={10}>
                            <TextField
                                label="Company Name*"
                                name="company_name"
                                fullWidth
                                margin="dense"
                                value={formData.company_name}
                                onChange={handleInputChange}
                                error={!!errors.company_name}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3, // Border color based on darkMode
                                        },
                                        '&:hover fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3, // Border color on hover
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3, // Border color when focused
                                        },
                                    },
                                }}
                                InputLabelProps={{ sx: { color: darkMode ? colors.f4 : colors.f4 } }}
                                inputProps={{ style: { color: darkMode ? colors.f2 : colors.f3 } }}
                            />
                            {errors.company_name && <FormHelperText error sx={{ color: darkMode ? colors.f2 : colors.f3 }}>{errors.company_name}</FormHelperText>}
                        </Grid>
                        {/* Phone Number */}
                        <Grid item xs={12} sm={10}>
                            <TextField
                                label="Phone Number*"
                                name="phone_number"
                                fullWidth
                                margin="dense"
                                value={formData.phone_number}
                                onChange={handleInputChange}
                                error={!!errors.phone_number}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3, // Border color based on darkMode
                                        },
                                        '&:hover fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3, // Border color on hover
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3, // Border color when focused
                                        },
                                    },
                                }}
                                InputLabelProps={{ sx: { color: darkMode ? colors.f4 : colors.f4 } }}
                                inputProps={{ style: { color: darkMode ? colors.f2 : colors.f3 } }}
                            />
                            {errors.phone_number && <FormHelperText error sx={{ color: darkMode ? colors.f2 : colors.f3 }}>{errors.phone_number}</FormHelperText>}
                        </Grid>
                        {/* Tariff */}
                        <Grid item xs={12} sm={10}>
                            <FormControl fullWidth margin="dense" error={!!errors.tariff}>
                                <InputLabel sx={{ color: darkMode ? colors.f4 : colors.f4 }}>Tariff*</InputLabel>
                                <Select
                                    name="tariff"
                                    value={formData.tariff}
                                    onChange={handleInputChange}
                                    label="Tariff"
                                    sx={{
                                        color: darkMode ? colors.f2 : colors.f3,
                                        '.MuiOutlinedInput-notchedOutline': {
                                            borderColor: darkMode ? colors.f2 : colors.f3,
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: darkMode ? colors.f2 : colors.f3,
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: darkMode ? colors.f2 : colors.f3,
                                        },
                                        '& .MuiSelect-icon': {
                                            color: darkMode ? colors.f2 : colors.f3, // Arrow color
                                        },
                                    }}
                                >
                                    <MenuItem value="LVG">Non-Domestic Low Voltage General Tariff</MenuItem>
                                    <MenuItem value="LVT">Non-Domestic Low Voltage ToU Tariff</MenuItem>
                                    <MenuItem value="MVG">Non-Domestic Medium Voltage General Tariff</MenuItem>
                                    <MenuItem value="MVT">Non-Domestic Medium Voltage ToU Tariff</MenuItem>
                                    <MenuItem value="HVG">Non-Domestic High Voltage General Tariff</MenuItem>
                                    <MenuItem value="HVT">Non-Domestic High Voltage ToU Tariff</MenuItem>
                                </Select>
                                {errors.tariff && (
                                    <FormHelperText sx={{ color: darkMode ? colors.f2 : colors.f3 }}>
                                        {errors.tariff}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        </Grid>
                        {/* UserRole */}
                        {!existingUser && (
                            <>
                                <Grid item xs={12} sm={10}>
                                    <FormControl fullWidth margin="dense" error={!!errors.user_role} >
                                        <InputLabel sx={{ color: darkMode ? colors.f4 : colors.f4 }}>User Role*</InputLabel>
                                        <Select
                                            name="user_role"
                                            value={formData.user_role}
                                            onChange={handleInputChange}
                                            label="User Role"
                                            sx={{
                                                color: darkMode ? colors.f2 : colors.f3,
                                                '.MuiOutlinedInput-notchedOutline': {
                                                    borderColor: darkMode ? colors.f2 : colors.f3,
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: darkMode ? colors.f2 : colors.f3,
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: darkMode ? colors.f2 : colors.f3,
                                                },
                                                '& .MuiSelect-icon': {
                                                    color: darkMode ? colors.f2 : colors.f3, // Arrow color
                                                },
                                            }}
                                        >
                                            <MenuItem value="user" sx={{ color: darkMode ? colors.f3 : colors.f3 }}>User</MenuItem>
                                            <MenuItem value="sitemanager" sx={{ color: darkMode ? colors.f3 : colors.f3 }}>Site Manager</MenuItem>
                                        </Select>
                                        {errors.user_role && (
                                            <FormHelperText sx={{ color: darkMode ? colors.f2 : colors.f3 }}>
                                                {errors.user_role}
                                            </FormHelperText>
                                        )}
                                    </FormControl>
                                </Grid>

                            </>
                        )}
                        {/* Owned By */}
                        {!existingUser ? (
                            <>
                            </>
                        ) : (
                            <>
                                <Grid item xs={12} sm={10}>
                                    <FormControl fullWidth margin="dense" error={!!errors.clients_owned}>
                                        <Autocomplete
                                            multiple
                                            options={clientList.filter((client) => client.access_level === 2)}
                                            getOptionLabel={(client) => client.company_name || ""}

                                            value={clientList.filter((c) =>
                                                formData.clients_owned?.map(Number).includes(c.client_id)
                                            )}

                                            onChange={(event, newValue) => {
                                                handleInputChange({
                                                    target: {
                                                        name: "clients_owned",
                                                        value: newValue.map((client) => client.client_id),
                                                    },
                                                });
                                            }}

                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Owned By"
                                                    variant="outlined"
                                                />
                                            )}
                                        />

                                        {errors.clients_owned && (
                                            <FormHelperText sx={{ color: darkMode ? colors.f2 : colors.f3 }}>
                                                {errors.clients_owned}
                                            </FormHelperText>
                                        )}
                                    </FormControl>
                                </Grid>
                            </>
                        )}
                        {/* user type */}
                        <Grid item xs={12} sm={10}>
                            <FormControl fullWidth margin="dense" error={!!errors.user_type}>
                                <InputLabel sx={{ color: darkMode ? colors.f4 : colors.f4 }}>User Type</InputLabel>
                                <Select
                                    name="user_type"
                                    value={formData.user_type}
                                    onChange={handleInputChange}
                                    label="user Type"
                                    sx={{
                                        color: darkMode ? colors.f2 : colors.f3,
                                        '.MuiOutlinedInput-notchedOutline': {
                                            borderColor: darkMode ? colors.f2 : colors.f3,
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: darkMode ? colors.f2 : colors.f3,
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: darkMode ? colors.f2 : colors.f3,
                                        },
                                        '& .MuiSelect-icon': {
                                            color: darkMode ? colors.f2 : colors.f3, // Arrow color
                                        },
                                    }}
                                >

                                    <MenuItem value={0}>eCOS</MenuItem>
                                    <MenuItem value={1}>MRS</MenuItem>
                                    <MenuItem value={2}>eCOS Plus</MenuItem>
                                    <MenuItem value={6}>Bess Designer</MenuItem>
                                </Select>
                                {errors.user_type && (
                                    <FormHelperText sx={{ color: darkMode ? colors.f2 : colors.f3 }}>
                                        {errors.user_type}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        </Grid>

                        {/* PPA type*/}
                        <Grid item xs={12} sm={10}>
                            <FormControl fullWidth margin="dense" error={!!errors.is_ppa}>
                                <InputLabel sx={{ color: darkMode ? colors.f4 : colors.f4 }}>PPA Type</InputLabel>
                                <Select
                                    name="is_ppa"
                                    value={formData.is_ppa}
                                    onChange={handleInputChange}
                                    label="PPA Type"
                                    sx={{
                                        color: darkMode ? colors.f2 : colors.f3,
                                        '.MuiOutlinedInput-notchedOutline': {
                                            borderColor: darkMode ? colors.f2 : colors.f3,
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: darkMode ? colors.f2 : colors.f3,
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: darkMode ? colors.f2 : colors.f3,
                                        },
                                        '& .MuiSelect-icon': {
                                            color: darkMode ? colors.f2 : colors.f3, // Arrow color
                                        },
                                    }}
                                >

                                    <MenuItem value={0}>PPA Subscriber</MenuItem>
                                    <MenuItem value={1}>PPA Provider</MenuItem>
                                </Select>
                                {errors.is_ppa && (
                                    <FormHelperText sx={{ color: darkMode ? colors.f2 : colors.f3 }}>
                                        {errors.is_ppa}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        </Grid>

                        {/* PPA Owned*/}
                        {!existingUser ? (
                            <>
                            </>
                        ) : (
                            <>
                                <Grid item xs={12} sm={10}>
                                    <FormControl fullWidth margin="dense" error={!!errors.ppa_owned_by}>
                                        <Autocomplete
                                            multiple
                                            options={clientList.filter((client) => client.is_ppa == 1)}
                                            getOptionLabel={(client) => client.company_name || ""}

                                            value={(() => {
                                                const ppaClients = clientList.filter((client) => client.is_ppa == 1);

                                                // Handle null or undefined
                                                if (!formData.ppa_owned_by && formData.ppa_owned_by !== 0) return [];

                                                // If it's null, return empty array
                                                if (formData.ppa_owned_by === null) return [];

                                                let ownedIds = [];

                                                // Rest of your existing logic...
                                                // Check if ppa_owned_by is a number (like 203)
                                                if (typeof formData.ppa_owned_by === 'number') {
                                                    ownedIds = [formData.ppa_owned_by];
                                                }
                                                // Check if it's a string (like "203" or "203,204")
                                                else if (typeof formData.ppa_owned_by === 'string' && formData.ppa_owned_by) {
                                                    if (formData.ppa_owned_by.includes(',')) {
                                                        ownedIds = formData.ppa_owned_by.split(',').map(id => Number(id.trim()));
                                                    } else {
                                                        ownedIds = [Number(formData.ppa_owned_by)];
                                                    }
                                                }
                                                // Check if it's an array
                                                else if (Array.isArray(formData.ppa_owned_by)) {
                                                    ownedIds = formData.ppa_owned_by.map(Number);
                                                }

                                                // Return matching client objects
                                                const matched = ppaClients.filter(client => ownedIds.includes(Number(client.client_id)));
                                                console.log("Matched PPA clients:", matched);
                                                return matched;
                                            })()}

                                            onChange={(event, newValue) => {
                                                const ppaIds = newValue.map(client => client.client_id);

                                                // ✅ This sends null when cleared
                                                const ppaValue = ppaIds.length === 0 ? null :
                                                    (ppaIds.length === 1 ? ppaIds[0] : ppaIds.join(','));

                                                handleInputChange({
                                                    target: {
                                                        name: "ppa_owned_by",
                                                        value: ppaValue,
                                                    },
                                                });
                                            }}

                                            isOptionEqualToValue={(option, value) =>
                                                Number(option.client_id) === Number(value.client_id)
                                            }

                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="PPA Owned"
                                                    variant="outlined"
                                                />
                                            )}
                                        />

                                        {errors.ppa_owned_by && (
                                            <FormHelperText sx={{ color: darkMode ? colors.f2 : colors.f3 }}>
                                                {errors.ppa_owned_by}
                                            </FormHelperText>
                                        )}
                                    </FormControl>
                                </Grid>
                            </>
                        )}

                        {/* Energy Supplier */}
                        <Grid item xs={12} sm={10}>
                            <FormControl fullWidth margin="dense" error={!!errors.energy_supplier}>
                                <InputLabel sx={{ color: darkMode ? colors.f4 : colors.f4 }}>Energy Supplier</InputLabel>
                                <Select
                                    name="energy_supplier"
                                    value={formData.energy_supplier}
                                    onChange={handleInputChange}
                                    label="Energy Supplier"
                                    sx={{
                                        color: darkMode ? colors.f2 : colors.f3,
                                        '.MuiOutlinedInput-notchedOutline': {
                                            borderColor: darkMode ? colors.f2 : colors.f3,
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: darkMode ? colors.f2 : colors.f3,
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: darkMode ? colors.f2 : colors.f3,
                                        },
                                        '& .MuiSelect-icon': {
                                            color: darkMode ? colors.f2 : colors.f3, // Arrow color
                                        },
                                    }}
                                >

                                    <MenuItem value={"TNB"}>TNB</MenuItem>
                                    <MenuItem value={"NUR"}>NUR</MenuItem>
                                </Select>
                                {errors.energy_supplier && (
                                    <FormHelperText sx={{ color: darkMode ? colors.f2 : colors.f3 }}>
                                        {errors.energy_supplier}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        </Grid>



                    </Grid>
                    {/* Company Address & Location */}
                    <Box sx={{ width: "30%", height: "100%" }}>

                        <LoadScript googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAP_API} libraries={mapLibraries}>
                            <Box sx={{ display: "flex", flexDirection: "column", width: "100%", height: "100%" }}>
                                <StandaloneSearchBox
                                    onLoad={(ref) => (searchBoxRef.current = ref)}
                                    onPlacesChanged={handlePlacesChanged}
                                >
                                    <TextField
                                        label="Company Address*"
                                        fullWidth
                                        margin="dense"
                                        sx={{
                                            mb: "2vh",
                                            color: darkMode ? colors.f2 : colors.f3, '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    borderColor: darkMode ? colors.f2 : colors.f3, // Border color based on darkMode
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: darkMode ? colors.f2 : colors.f3, // Border color on hover
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: darkMode ? colors.f2 : colors.f3, // Border color when focused
                                                },
                                            },
                                        }}
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        error={!!errors.company_address}
                                        InputLabelProps={{ sx: { color: darkMode ? colors.f4 : colors.f4 } }}
                                        inputProps={{ style: { color: darkMode ? colors.f2 : colors.f3 } }}
                                    />
                                </StandaloneSearchBox>

                                <GoogleMap
                                    mapContainerStyle={containerStyle}
                                    center={mapCenter}
                                    zoom={14}
                                    options={{ disableDefaultUI: true, zoomControl: true }}
                                >
                                    <Marker position={mapCenter} />
                                </GoogleMap>


                            </Box>
                        </LoadScript>
                        {errors.company_address && <FormHelperText error sx={{ color: darkMode ? colors.f2 : colors.f3 }}>{errors.company_address}</FormHelperText>}
                    </Box>
                    {/* Image Upload */}
                    <Box
                        sx={{
                            width: "40%",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "flex-start",
                            alignItems: "center",
                            paddingLeft: "5%",
                            paddingTop: "1%",
                        }}
                    >
                        {/* Image Upload */}
                        <Box sx={{ height: "50%", width: "100%", display: "flex", flexDirection: "column", justifyContent: "flex-start", alignItems: "center", paddingTop: "2%" }}>
                            <Typography sx={{ fontSize: "1vw", padding: "1%", color: darkMode ? colors.f2 : colors.f3 }}>
                                Please Select An Image for Account
                            </Typography>
                            {selectedImage
                                ? (
                                    <Box
                                        component="img"
                                        src={URL.createObjectURL(selectedImage)}
                                        alt="New preview"
                                        sx={{ width: '60%', maxHeight: '60%', objectFit: 'cover', borderRadius: '1vw' }}
                                    />
                                ) : existingImage ? (
                                    <Box
                                        component="img"
                                        src={existingImage}
                                        alt="Existing photo"
                                        sx={{ width: '60%', maxHeight: '60%', objectFit: 'fix', borderRadius: '1vw' }}
                                    />
                                ) : (
                                    <Typography color="error">No image on file.</Typography>
                                )
                            }
                            {!selectedImage && existingUser && (
                                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: darkMode ? colors.f4 : colors.f3 }}>
                                    {existingImage ? 'Showing existing photo' : 'No photo on file'}
                                </Typography>
                            )}
                            <input
                                accept="image/jpeg,image/jpg,image/png"
                                type="file"
                                onChange={handleImageChange}
                                style={{
                                    opacity: 0,
                                    position: "absolute",
                                    width: "0.1px",
                                    height: "0.1px",
                                    overflow: "hidden",
                                    zIndex: -1,
                                }}
                                id="hidden-file-input"
                            />

                            <button
                                onClick={() => document.getElementById("hidden-file-input").click()}
                                style={{
                                    padding: "2% 4%",
                                    backgroundColor: darkMode ? colors.d4 : colors.l4,
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: "0.5vw",
                                    cursor: "pointer",
                                    fontSize: "0.8vw",
                                    marginTop: "3%",
                                }}
                            >
                                Upload Image
                            </button>
                        </Box>

                        {/* Logo Upload */}
                        <Box sx={{ height: "50%", width: "100%", display: "flex", flexDirection: "column", justifyContent: "flex-start", alignItems: "center", paddingTop: "20%" }}>
                            <Typography sx={{ fontSize: "1vw", padding: "1%", color: darkMode ? colors.f2 : colors.f3 }}>
                                Please Select An Image for Logo
                            </Typography>
                            {selectedLogo
                                ? (
                                    <Box
                                        component="img"
                                        src={URL.createObjectURL(selectedLogo)}
                                        alt="New preview"
                                        sx={{ width: '60%', maxHeight: '60%', objectFit: 'cover', borderRadius: '1vw' }}
                                    />
                                ) : existingLogo ? (
                                    <Box
                                        component="img"
                                        src={existingLogo}
                                        alt="Existing photo"
                                        sx={{ width: '60%', maxHeight: '60%', objectFit: 'fix', borderRadius: '1vw' }}
                                    />
                                ) : (
                                    <Typography color="error">No image on file.</Typography>
                                )
                            }
                            {!selectedLogo && existingUser && (
                                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: darkMode ? colors.f4 : colors.f3 }}>
                                    {existingLogo ? 'Showing existing photo' : 'No photo on file'}
                                </Typography>
                            )}
                            <input
                                accept="image/jpeg,image/jpg,image/png"
                                type="file"
                                onChange={handleLogoChange}
                                style={{
                                    opacity: 0,
                                    position: "absolute",
                                    width: "0.1px",
                                    height: "0.1px",
                                    overflow: "hidden",
                                    zIndex: -1,
                                }}
                                id="hidden-logo-input"
                            />

                            <button
                                onClick={() => document.getElementById("hidden-logo-input").click()}
                                style={{
                                    padding: "2% 4%",
                                    backgroundColor: darkMode ? colors.d4 : colors.l4,
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: "0.5vw",
                                    cursor: "pointer",
                                    fontSize: "0.8vw",
                                    marginTop: "3%",
                                }}
                            >
                                Upload Logo
                            </button>
                        </Box>
                    </Box>

                </Box>

                <Divider sx={{ borderColor: "lightgray", borderWidth: "0.5%", width: "100%", margin: "1% 0 1% 0 " }} />

                {/*Solar system Details*/}
                <Box sx={{ display: "flex", width: "100%", height: "100%" }}><Typography sx={{ fontSize: "1.3vw", fontWeight: "bold", pl: "2%", color: darkMode ? colors.f2 : colors.f3, }}>Solar System Details</Typography></Box>
                <Box sx={{ width: "100%", height: "100%", padding: "1%" }}>
                    <Grid container sx={{ rowGap: "3vh", columnGap: "5%" }}>

                        {/*Solar System Size*/}
                        <Grid item xs={12} sm={3.5}>
                            <TextField
                                label="Solar System Size"
                                name="solar_system_size"
                                fullWidth
                                margin="dense"
                                value={formData.solar_system_size}
                                onChange={handleInputChange}
                                error={!!errors.solar_system_size}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3,
                                        },
                                    },
                                }}
                                InputLabelProps={{ sx: { color: darkMode ? colors.f4 : colors.f4 } }}
                                inputProps={{ style: { color: darkMode ? colors.f2 : colors.f3 } }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Typography sx={{ color: darkMode ? colors.f4 : colors.f1 }}>
                                                kWp
                                            </Typography>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            {errors.solar_system_size && (
                                <FormHelperText error>{errors.solar_system_size}</FormHelperText>
                            )}
                        </Grid>
                        {/*Solar Inverter Size*/}
                        <Grid item xs={12} sm={3.5}>
                            <TextField
                                label="Solar Inverter Size"
                                name="solar_inverter_size"
                                fullWidth
                                margin="dense"
                                value={formData.solar_inverter_size}
                                onChange={handleInputChange}
                                error={!!errors.solar_inverter_size}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3, // Border color based on darkMode
                                        },
                                        '&:hover fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3, // Border color on hover
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3, // Border color when focused
                                        },
                                    },
                                }}
                                InputLabelProps={{ sx: { color: darkMode ? colors.f4 : colors.f4 } }}
                                inputProps={{ style: { color: darkMode ? colors.f2 : colors.f3 } }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Typography sx={{ color: darkMode ? colors.f4 : colors.f1 }}>
                                                kWac
                                            </Typography>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            {errors.solar_inverter_size && (
                                <FormHelperText error>{errors.solar_inverter_size}</FormHelperText>
                            )}
                        </Grid>
                        {/*Installation Date*/}
                        <Grid item xs={12} sm={3.5}>
                            <TextField
                                label="Installation Date"
                                name="solar_install_date"
                                type="date"
                                fullWidth
                                margin="dense"
                                value={formData.solar_install_date}
                                onChange={handleInputChange}
                                error={!!errors.solar_install_date}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3,
                                        },
                                        '& input': {
                                            color: darkMode ? colors.f2 : colors.f3,
                                            '&::-webkit-calendar-picker-indicator': {
                                                filter: darkMode ? 'invert(1)' : 'none',
                                            },
                                        },
                                    },
                                }}
                                InputLabelProps={{
                                    shrink: true,
                                    sx: { color: darkMode ? colors.f4 : colors.f4 },
                                }}
                                inputProps={{
                                    style: {
                                        color: darkMode ? colors.f2 : colors.f3,
                                    },
                                }}
                            />
                            {errors.solar_install_date && (
                                <FormHelperText error>{errors.solar_install_date}</FormHelperText>
                            )}
                        </Grid>
                        {/*P90*/}
                        <Grid item xs={12} sm={3.5}>
                            <TextField
                                label="P90"
                                name="p90_estimate"
                                fullWidth
                                margin="dense"
                                value={formData.p90_estimate}
                                onChange={handleInputChange}
                                error={!!errors.p90_estimate}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3,
                                        },
                                        '& input': {
                                            color: darkMode ? colors.f2 : colors.f3,
                                        },
                                    },
                                }}
                                InputLabelProps={{ sx: { color: darkMode ? colors.f4 : colors.f4 } }}
                                inputProps={{
                                    style: {
                                        color: darkMode ? colors.f2 : colors.f3,
                                    },
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Typography sx={{ color: darkMode ? colors.f4 : colors.f1 }}>
                                                MWh
                                            </Typography>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            {errors.p90_estimate && (
                                <FormHelperText error>{errors.p90_estimate}</FormHelperText>
                            )}
                        </Grid>
                        {/*P50*/}
                        <Grid item xs={12} sm={3.5}>
                            <TextField
                                label="P50"
                                name="p50_estimate"
                                fullWidth
                                margin="dense"
                                value={formData.p50_estimate}
                                onChange={handleInputChange}
                                error={!!errors.p50_estimate}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3,
                                        },
                                        '& input': {
                                            color: darkMode ? colors.f2 : colors.f3,
                                        },
                                    },
                                }}
                                InputLabelProps={{ sx: { color: darkMode ? colors.f4 : colors.f4 } }}
                                inputProps={{
                                    style: {
                                        color: darkMode ? colors.f2 : colors.f3,
                                    },
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Typography sx={{ color: darkMode ? colors.f4 : colors.f1 }}>
                                                MWh
                                            </Typography>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            {errors.p50_estimate && (
                                <FormHelperText error>{errors.p50_estimate}</FormHelperText>
                            )}
                        </Grid>
                        {/*Solar Inverter Brand*/}
                        <Grid item xs={12} sm={3.5}>
                            <TextField
                                label="Solar Inverter Brand"
                                name="solar_inverter_brand"
                                fullWidth
                                margin="dense"
                                value={formData.solar_inverter_brand}
                                onChange={handleInputChange}
                                error={!!errors.solar_inverter_brand}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3, // Border color based on darkMode
                                        },
                                        '&:hover fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3, // Border color on hover
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3, // Border color when focused
                                        },
                                    },
                                }}
                                InputLabelProps={{ sx: { color: darkMode ? colors.f4 : colors.f4 } }}
                                inputProps={{ style: { color: darkMode ? colors.f2 : colors.f3 } }}
                            />
                            {errors.solar_inverter_brand && <FormHelperText error>{errors.solar_inverter_brand}</FormHelperText>}
                        </Grid>
                        {/*Solar Panel Brand*/}
                        <Grid item xs={12} sm={3.5}>
                            <TextField
                                label="Solar Panel Brand"
                                name="solar_panel_brand"
                                fullWidth
                                margin="dense"
                                value={formData.solar_panel_brand}
                                onChange={handleInputChange}
                                error={!!errors.solar_panel_brand}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3, // Border color based on darkMode
                                        },
                                        '&:hover fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3, // Border color on hover
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3, // Border color when focused
                                        },
                                    },
                                }}
                                InputLabelProps={{ sx: { color: darkMode ? colors.f4 : colors.f4 } }}
                                inputProps={{ style: { color: darkMode ? colors.f2 : colors.f3 } }}
                            />
                            {errors.solar_panel_brand && <FormHelperText error>{errors.solar_panel_brand}</FormHelperText>}
                        </Grid>
                        {/*Installation PIC*/}
                        <Grid item xs={12} sm={3.5}>
                            <TextField
                                label="Installation PIC"
                                name="solar_install_pic"
                                fullWidth
                                margin="dense"
                                value={formData.solar_install_pic}
                                onChange={handleInputChange}
                                error={!!errors.solar_install_pic}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3, // Border color based on darkMode
                                        },
                                        '&:hover fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3, // Border color on hover
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3, // Border color when focused
                                        },
                                    },
                                }}
                                InputLabelProps={{ sx: { color: darkMode ? colors.f4 : colors.f4 } }}
                                inputProps={{ style: { color: darkMode ? colors.f2 : colors.f3 } }}
                            />
                            {errors.solar_install_pic && <FormHelperText error>{errors.solar_install_pic}</FormHelperText>}
                        </Grid>
                        {/*Peak Sun Hour*/}
                        <Grid item xs={12} sm={3.5}>
                            <TextField
                                name="psh_estimate"
                                fullWidth
                                margin="dense"
                                value={`Peak Sun Hour = ${formData.psh_estimate}`}
                                InputProps={{
                                    readOnly: true,
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: darkMode ? colors.f3 : colors.f2,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: darkMode ? colors.f3 : colors.f2,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: darkMode ? colors.f3 : colors.f2,
                                        },
                                    },
                                }}
                                InputLabelProps={{ sx: { color: darkMode ? colors.f4 : colors.f4 } }}
                                inputProps={{ style: { color: darkMode ? colors.f2 : colors.f3 } }}
                            />

                        </Grid>
                        {/* Container row for Note and PDF upload */}
                        <Grid container spacing={2} alignItems="flex-start">
                            {/* Note field */}
                            <Grid item xs={12} sm={7.65}>
                                <TextField
                                    label="Note"
                                    name="solar_notes"
                                    fullWidth
                                    margin="dense"
                                    value={formData.solar_notes}
                                    onChange={handleInputChange}
                                    error={!!errors.solar_notes}
                                    multiline
                                    minRows={8}
                                    maxRows={10}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: darkMode ? colors.f2 : colors.f3,
                                            },
                                            '&:hover fieldset': {
                                                borderColor: darkMode ? colors.f2 : colors.f3,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: darkMode ? colors.f2 : colors.f3,
                                            },
                                        },
                                    }}
                                    InputLabelProps={{ sx: { color: darkMode ? colors.f4 : colors.f4 } }}
                                    inputProps={{ style: { color: darkMode ? colors.f2 : colors.f3 } }}
                                />
                                {errors.solar_notes && (
                                    <FormHelperText error>{errors.solar_notes}</FormHelperText>
                                )}
                            </Grid>

                            {/* PDF upload section */}
                            <Grid item xs={12} sm={4}>
                                <Box
                                    sx={{
                                        width: "100%",
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        mt: "10%",
                                    }}
                                >
                                    <Typography sx={{ fontSize: "1vw", padding: "1%", color: darkMode ? colors.f2 : colors.f3 }}>
                                        Please Upload PVSyst PDF Report
                                    </Typography>

                                    {selectedPDF ? (
                                        <Typography sx={{ fontSize: "0.9vw", mt: 2 }}>
                                            Selected File: {selectedPDF.name}
                                        </Typography>
                                    ) : existingPDF ? (
                                        <Typography sx={{ fontSize: "0.9vw", mt: 2 }}>
                                            Existing File:{" "}
                                            <a
                                                href={existingPDF}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ color: "blue", textDecoration: "underline" }} // optional underline
                                            >
                                                View PDF
                                            </a>
                                        </Typography>

                                    ) : (
                                        <Typography color="error">No PDF file exists.</Typography>
                                    )}

                                    {!selectedPDF && existingUser && (
                                        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: darkMode ? colors.f4 : colors.f3 }}>
                                            {existingPDF ? 'Showing existing PDF' : 'No PDF on file'}
                                        </Typography>
                                    )}

                                    <input
                                        accept="application/pdf"
                                        type="file"
                                        onChange={handlePDFChange}
                                        style={{
                                            opacity: 0,
                                            position: "absolute",
                                            width: "0.1px",
                                            height: "0.1px",
                                            overflow: "hidden",
                                            zIndex: -1,
                                        }}
                                        id="upload-pdf-input" // Make sure this ID is unique
                                    />

                                    <button
                                        onClick={() => document.getElementById("upload-pdf-input").click()}
                                        style={{
                                            padding: "2% 4%",
                                            backgroundColor: darkMode ? colors.d4 : colors.l4,
                                            color: "#fff",
                                            border: "none",
                                            borderRadius: "0.5vw",
                                            cursor: "pointer",
                                            fontSize: "0.8vw",
                                            marginTop: "3%",
                                        }}
                                    >
                                        Upload PDF
                                    </button>
                                </Box>
                            </Grid>
                        </Grid>



                    </Grid>
                </Box>

                <Divider sx={{ borderColor: "lightgray", borderWidth: "0.5%", width: "100%", margin: "1% 0 1% 0 " }} />

                {/*Battery system Details*/}
                <Box sx={{ display: "flex", width: "100%", height: "100%" }}><Typography sx={{ fontSize: "1.3vw", fontWeight: "bold", pl: "2%", color: darkMode ? colors.f2 : colors.f3, }}>Battery System Details</Typography></Box>
                <Box sx={{ width: "100%", height: "100%", padding: "1%" }}>
                    <Grid container sx={{ rowGap: "3vh", columnGap: "5%" }}>
                        {/*Battery System Size*/}
                        <Grid item xs={12} sm={3.5}>
                            <TextField
                                label="Battery Capacity Size"
                                name="battery_capacity_size"
                                fullWidth
                                margin="dense"
                                value={formData.battery_capacity_size}
                                onChange={handleInputChange}
                                error={!!errors.battery_capacity_size}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3,
                                        },
                                        '& input': {
                                            color: darkMode ? colors.f2 : colors.f3,
                                        },
                                    },
                                }}
                                InputLabelProps={{ sx: { color: darkMode ? colors.f4 : colors.f4 } }}
                                inputProps={{
                                    style: {
                                        color: darkMode ? colors.f2 : colors.f3,
                                    },
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Typography sx={{ color: darkMode ? colors.f4 : colors.f1 }}>
                                                kWh
                                            </Typography>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            {errors.battery_capacity_size && (
                                <FormHelperText error>{errors.battery_capacity_size}</FormHelperText>
                            )}
                        </Grid>
                        {/*Battery Inveter Size*/}
                        <Grid item xs={12} sm={3.5}>
                            <TextField
                                label="Battery Inverter Size"
                                name="battery_inverter_size"
                                fullWidth
                                margin="dense"
                                value={formData.battery_inverter_size}
                                onChange={handleInputChange}
                                error={!!errors.battery_inverter_size}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3,
                                        },
                                        '& input': {
                                            color: darkMode ? colors.f2 : colors.f3,
                                        },
                                    },
                                }}
                                InputLabelProps={{ sx: { color: darkMode ? colors.f4 : colors.f4 } }}
                                inputProps={{
                                    style: {
                                        color: darkMode ? colors.f2 : colors.f3,
                                    },
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Typography sx={{ color: darkMode ? colors.f4 : colors.f1 }}>
                                                kWac
                                            </Typography>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            {errors.battery_inverter_size && (
                                <FormHelperText error>{errors.battery_inverter_size}</FormHelperText>
                            )}
                        </Grid>
                        {/*Installation Date*/}
                        <Grid item xs={12} sm={3.5}>
                            <TextField
                                label="Installation Date"
                                name="battery_install_date"
                                type="date"
                                fullWidth
                                margin="dense"
                                value={formData.battery_install_date}
                                onChange={handleInputChange}
                                error={!!errors.battery_install_date}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3,
                                        },
                                        '& input': {
                                            color: darkMode ? colors.f2 : colors.f3,
                                            // Force icon color for some browsers (esp. Chromium-based)
                                            '&::-webkit-calendar-picker-indicator': {
                                                filter: darkMode ? 'invert(1)' : 'none',
                                            },
                                        },
                                    },
                                }}
                                InputLabelProps={{
                                    shrink: true,
                                    sx: { color: darkMode ? colors.f4 : colors.f4 },
                                }}
                                inputProps={{
                                    style: {
                                        color: darkMode ? colors.f2 : colors.f3,
                                    },
                                }}
                            />
                            {errors.battery_install_date && (
                                <FormHelperText error>{errors.battery_install_date}</FormHelperText>
                            )}
                        </Grid>
                        {/*Battery Model (DC)*/}
                        <Grid item xs={12} sm={3.5}>
                            <TextField
                                label="Battery Model (DC)"
                                name="battery_dc_brand"
                                fullWidth
                                margin="dense"
                                value={formData.battery_dc_brand}
                                onChange={handleInputChange}
                                error={!!errors.battery_dc_brand}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3, // Border color based on darkMode
                                        },
                                        '&:hover fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3, // Border color on hover
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3, // Border color when focused
                                        },
                                    },
                                }}
                                InputLabelProps={{ sx: { color: darkMode ? colors.f4 : colors.f4 } }}
                                inputProps={{ style: { color: darkMode ? colors.f2 : colors.f3 } }}
                            />
                            {errors.battery_dc_brand && <FormHelperText error>{errors.battery_dc_brand}</FormHelperText>}
                        </Grid>
                        {/*Battery Model (AC)*/}
                        <Grid item xs={12} sm={3.5}>
                            <TextField
                                label="Inverter Model (AC)"
                                name="battery_inverter_brand"
                                fullWidth
                                margin="dense"
                                value={formData.battery_inverter_brand}
                                onChange={handleInputChange}
                                error={!!errors.battery_inverter_brand}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3, // Border color based on darkMode
                                        },
                                        '&:hover fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3, // Border color on hover
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3, // Border color when focused
                                        },
                                    },
                                }}
                                InputLabelProps={{ sx: { color: darkMode ? colors.f4 : colors.f4 } }}
                                inputProps={{ style: { color: darkMode ? colors.f2 : colors.f3 } }}
                            />
                            {errors.battery_inverter_brand && <FormHelperText error>{errors.battery_inverter_brand}</FormHelperText>}
                        </Grid>
                        {/*Battery C Rating*/}
                        <Grid item xs={12} sm={3.5}>
                            <TextField
                                label="Battery C Rating"
                                name="battery_c_rating"
                                fullWidth
                                margin="dense"
                                value={formData.battery_c_rating}
                                onChange={handleInputChange}
                                error={!!errors.battery_c_rating}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3, // Border color based on darkMode
                                        },
                                        '&:hover fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3, // Border color on hover
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3, // Border color when focused
                                        },
                                    },
                                }}
                                InputLabelProps={{ sx: { color: darkMode ? colors.f4 : colors.f4 } }}
                                inputProps={{ style: { color: darkMode ? colors.f2 : colors.f3 } }}
                            />
                            {errors.battery_c_rating && <FormHelperText error>{errors.battery_c_rating}</FormHelperText>}
                        </Grid>
                        {/*Installation PIC*/}
                        <Grid item xs={12} sm={3.5}>
                            <TextField
                                label="Installation PIC"
                                name="battery_install_pic"
                                fullWidth
                                margin="dense"
                                value={formData.battery_install_pic}
                                onChange={handleInputChange}
                                error={!!errors.battery_install_pic}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3, // Border color based on darkMode
                                        },
                                        '&:hover fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3, // Border color on hover
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3, // Border color when focused
                                        },
                                    },
                                }}
                                InputLabelProps={{ sx: { color: darkMode ? colors.f4 : colors.f4 } }}
                                inputProps={{ style: { color: darkMode ? colors.f2 : colors.f3 } }}
                            />
                            {errors.battery_install_pic && <FormHelperText error>{errors.battery_install_pic}</FormHelperText>}
                        </Grid>
                        {/*Note*/}
                        <Grid item xs={12} sm={11.7}>
                            <TextField
                                label="Note"
                                name="battery_notes"
                                fullWidth
                                margin="dense"
                                value={formData.battery_notes}
                                onChange={handleInputChange}
                                error={!!errors.battery_notes}
                                multiline
                                minRows={5}
                                maxRows={7}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3, // Border color based on darkMode
                                        },
                                        '&:hover fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3, // Border color on hover
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3, // Border color when focused
                                        },
                                    },
                                }}
                                InputLabelProps={{ sx: { color: darkMode ? colors.f4 : colors.f4 } }}
                                inputProps={{ style: { color: darkMode ? colors.f2 : colors.f3 } }}
                            />
                            {errors.battery_notes && <FormHelperText error>{errors.battery_notes}</FormHelperText>}
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{ borderColor: "lightgray", borderWidth: "0.5%", width: "100%", margin: "1% 0 1% 0 " }} />

                {/*license update Details*/}
                <Box sx={{ display: "flex", width: "100%", height: "100%" }}><Typography sx={{ fontSize: "1.3vw", fontWeight: "bold", pl: "2%", color: darkMode ? colors.f2 : colors.f3, }}>License Update Details</Typography></Box>
                <Box sx={{ width: "100%", height: "100%", padding: "1%" }}>
                    <Grid container sx={{ rowGap: "3vh", columnGap: "5%" }}>

                        {/*license start*/}
                        <Grid item xs={12} sm={3.5}>
                            <TextField
                                label="License Start Date"
                                name="license_start_date"
                                type="date"
                                fullWidth
                                margin="dense"
                                value={formData.license_start_date}
                                onChange={handleInputChange}
                                error={!!errors.license_start_date}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3,
                                        },
                                        '& input': {
                                            color: darkMode ? colors.f2 : colors.f3,
                                            // Force icon color for some browsers (esp. Chromium-based)
                                            '&::-webkit-calendar-picker-indicator': {
                                                filter: darkMode ? 'invert(1)' : 'none',
                                            },
                                        },
                                    },
                                }}
                                InputLabelProps={{
                                    shrink: true,
                                    sx: { color: darkMode ? colors.f4 : colors.f4 },
                                }}
                                inputProps={{
                                    style: {
                                        color: darkMode ? colors.f2 : colors.f3,
                                    },
                                }}
                            />
                            {errors.license_start_date && (
                                <FormHelperText error>{errors.license_start_date}</FormHelperText>
                            )}
                        </Grid>
                        {/*license due*/}
                        <Grid item xs={12} sm={3.5}>
                            <TextField
                                label="License Due Date"
                                name="license_due_date"
                                type="date"
                                fullWidth
                                margin="dense"
                                value={formData.license_due_date}
                                onChange={handleInputChange}
                                error={!!errors.license_due_date}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3,
                                        },
                                        '& input': {
                                            color: darkMode ? colors.f2 : colors.f3,
                                            // Force icon color for some browsers (esp. Chromium-based)
                                            '&::-webkit-calendar-picker-indicator': {
                                                filter: darkMode ? 'invert(1)' : 'none',
                                            },
                                        },
                                    },
                                }}
                                InputLabelProps={{
                                    shrink: true,
                                    sx: { color: darkMode ? colors.f4 : colors.f4 },
                                }}
                                inputProps={{
                                    style: {
                                        color: darkMode ? colors.f2 : colors.f3,
                                    },
                                }}
                            />
                            {errors.license_due_date && (
                                <FormHelperText error>{errors.license_due_date}</FormHelperText>
                            )}
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{ borderColor: "lightgray", borderWidth: "0.5%", width: "100%", margin: "1% 0 1% 0 " }} />

                {/*eCOS Details*/}
                <Box sx={{ display: "flex", width: "100%", height: "100%" }}><Typography sx={{ fontSize: "1.3vw", fontWeight: "bold", pl: "2%", color: darkMode ? colors.f2 : colors.f3, }}>eCOS Installation Date *</Typography></Box>
                <Box sx={{ width: "100%", height: "100%", padding: "1%" }}>
                    <Grid container sx={{ rowGap: "3vh", columnGap: "5%" }}>
                        {/* eCOS Installation Date */}
                        <Grid item xs={12} sm={3.5}>
                            <TextField
                                label="eCOS Installation Date"
                                name="smartmeter_install_date"
                                type="date"
                                fullWidth
                                margin="dense"
                                value={formData.smartmeter_install_date}
                                onChange={handleInputChange}
                                error={!!errors.smartmeter_install_date}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: darkMode ? colors.f2 : colors.f3,
                                        },
                                        '& input': {
                                            color: darkMode ? colors.f2 : colors.f3,
                                            '&::-webkit-calendar-picker-indicator': {
                                                filter: darkMode ? 'invert(1)' : 'none',
                                            },
                                        },
                                    },
                                }}
                                InputLabelProps={{
                                    shrink: true,
                                    sx: { color: darkMode ? colors.f4 : colors.f4 },
                                }}
                                inputProps={{
                                    style: {
                                        color: darkMode ? colors.f2 : colors.f3,
                                    },
                                }}
                            />
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{ borderColor: "lightgray", borderWidth: "0.5%", width: "100%", margin: "1% 0 1% 0 " }} />

                {/*Meters*/}
                <Box sx={{ display: "flex", width: "100%", height: "100%" }}><Typography sx={{ fontSize: "1.3vw", fontWeight: "bold", pl: "2%", color: darkMode ? colors.f2 : colors.f3, }}>Smart Meters</Typography></Box>
                <Box sx={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "flex-start", flexDirection: "column", padding: "1%" }}>
                    <Box
                        sx={{ width: "100%", display: "flex", justifyContent: "flex-start", }}>
                        <Button
                            variant="contained"
                            onClick={addMeter}
                            sx={{ width: "fit-content", backgroundColor: darkMode ? colors.l6 : colors.f3, color: darkMode ? colors.f3 : colors.f2, ml: "1.1%" }}>
                            + Add Meter
                        </Button>
                    </Box>

                    {meters.map((meter, idx) => (
                        <Grid
                            container
                            spacing={2}
                            key={idx}
                            id={`meter-${idx}`}
                            alignItems="flex-start"  // 🔧 Ensures top alignment for all items
                            sx={{ mt: 1, color: darkMode ? colors.f4 : colors.f4 }}
                        >
                            {/* Index Label */}
                            <Grid item xs={12} sm={0.5}>
                                <Typography
                                    sx={{
                                        fontWeight: "bold",
                                        fontSize: "1vw",
                                        color: darkMode ? colors.f2 : colors.f3,
                                        pt: "40%",
                                        pl: "20%"


                                    }}
                                >
                                    #{idx + 1}
                                </Typography>
                            </Grid>
                            {/* Serial Number */}
                            <Grid item xs={12} sm={1.5}>
                                <TextField
                                    label="Serial Number"
                                    value={meter.serial_number}
                                    onChange={e => updateMeterField(idx, "serial_number", e.target.value)}
                                    fullWidth
                                    margin="dense"
                                    error={meterErrors[idx]?.serial_number}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: darkMode ? colors.f2 : colors.f3,
                                            },
                                            '&:hover fieldset': {
                                                borderColor: darkMode ? colors.f2 : colors.f3,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: darkMode ? colors.f2 : colors.f3,
                                            },
                                        },
                                    }}
                                    InputLabelProps={{ sx: { color: darkMode ? colors.f4 : colors.f4 } }}
                                    inputProps={{ style: { color: darkMode ? colors.f2 : colors.f3 } }}
                                />
                                {meterErrors[idx]?.serial_number && (
                                    <FormHelperText error>Required</FormHelperText>
                                )}
                            </Grid>

                            {/* Brand */}
                            <Grid item xs={12} sm={1.5}>
                                <FormControl
                                    fullWidth
                                    margin="dense"
                                    error={meterErrors[idx]?.brand}
                                >
                                    <InputLabel sx={{ color: darkMode ? colors.f4 : colors.f4 }}>Brand</InputLabel>
                                    <Select
                                        value={meter.brand}
                                        onChange={e => updateMeterField(idx, "brand", e.target.value)}
                                        label="Brand"
                                        sx={{
                                            color: darkMode ? colors.f2 : colors.f3,
                                            '.MuiOutlinedInput-notchedOutline': {
                                                borderColor: darkMode ? colors.f2 : colors.f3,
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: darkMode ? colors.f2 : colors.f3,
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: darkMode ? colors.f2 : colors.f3,
                                            },
                                            '& .MuiSelect-icon': {
                                                color: darkMode ? colors.f2 : colors.f3, // Arrow color
                                            },
                                        }}
                                    >
                                        <MenuItem value="Acrel">Acrel</MenuItem>
                                        <MenuItem value="Amsolar">Amsolar</MenuItem>
                                    </Select>
                                </FormControl>
                                {meterErrors[idx]?.brand && <FormHelperText error>Required</FormHelperText>}
                            </Grid>

                            {/* Model */}
                            <Grid item xs={12} sm={2}>
                                <FormControl
                                    fullWidth
                                    disabled={!meter.brand}
                                    margin="dense"
                                    error={meterErrors[idx]?.model}
                                >
                                    <InputLabel sx={{ color: darkMode ? colors.f4 : colors.f4 }}>
                                        {meter.brand ? "Model" : "Select brand first"}
                                    </InputLabel>
                                    <Select
                                        value={meter.model}
                                        onChange={e => updateMeterField(idx, "model", e.target.value)}
                                        label="Model"
                                        sx={{
                                            color: darkMode ? colors.f2 : colors.f3,
                                            '.MuiOutlinedInput-notchedOutline': { borderColor: darkMode ? colors.f2 : colors.f3 },
                                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: darkMode ? colors.f2 : colors.f3 },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: darkMode ? colors.f2 : colors.f3 },
                                            '& .MuiSelect-icon': { color: darkMode ? colors.f2 : colors.f3 },
                                        }}
                                    >
                                        {(brandModelMap[meter.brand] || []).map(m => (
                                            <MenuItem key={m} value={m}>{m}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                {meterErrors[idx]?.model && <FormHelperText error>Required</FormHelperText>}
                            </Grid>

                            {/* Usage */}
                            <Grid item xs={12} sm={2}>
                                <FormControl
                                    fullWidth
                                    margin="dense"
                                    error={meterErrors[idx]?.usage}
                                >
                                    <InputLabel sx={{ color: darkMode ? colors.f4 : colors.f4 }}>Usage</InputLabel>
                                    <Select
                                        value={meter.usage}
                                        onChange={e => updateMeterField(idx, "usage", e.target.value)}
                                        label="Usage"
                                        sx={{
                                            color: darkMode ? colors.f2 : colors.f3,
                                            '.MuiOutlinedInput-notchedOutline': { borderColor: darkMode ? colors.f2 : colors.f3 },
                                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: darkMode ? colors.f2 : colors.f3 },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: darkMode ? colors.f2 : colors.f3 },
                                            '& .MuiSelect-icon': { color: darkMode ? colors.f2 : colors.f3 },
                                        }}
                                    >
                                        <MenuItem value="Solar">Solar</MenuItem>
                                        <MenuItem value="Grid">Grid</MenuItem>
                                        <MenuItem value="DG">Diesel Generator</MenuItem>
                                        <MenuItem value="HVAC">HVAC</MenuItem>
                                        <MenuItem value="AirComp">Air Compressor</MenuItem>
                                        <MenuItem value="Chiller">Chiller</MenuItem>
                                        <MenuItem value="Motor">Motor</MenuItem>
                                        <MenuItem value="Others">Others</MenuItem>
                                    </Select>
                                </FormControl>
                                {meterErrors[idx]?.usage && <FormHelperText error>Required</FormHelperText>}
                            </Grid>

                            {/* Description */}
                            <Grid item xs={12} sm={2}>
                                <TextField
                                    label="Description"
                                    value={meter.description}
                                    onChange={e => updateMeterField(idx, "description", e.target.value)}
                                    fullWidth
                                    margin="dense"
                                    error={meterErrors[idx]?.description}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: darkMode ? colors.f2 : colors.f3,
                                            },
                                            '&:hover fieldset': {
                                                borderColor: darkMode ? colors.f2 : colors.f3,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: darkMode ? colors.f2 : colors.f3,
                                            },
                                        },
                                    }}
                                    InputLabelProps={{ sx: { color: darkMode ? colors.f4 : colors.f4 } }}
                                    inputProps={{ style: { color: darkMode ? colors.f2 : colors.f3 } }}
                                />
                                {meterErrors[idx]?.description && (
                                    <FormHelperText error>Required</FormHelperText>
                                )}
                            </Grid>

                            {/* Installation Data */}
                            <Grid item xs={12} sm={2}>
                                <TextField
                                    label="Installation Date"
                                    type="date"
                                    fullWidth
                                    margin="dense"
                                    value={meter.sm_installation_date}
                                    onChange={(e) => updateMeterField(idx, "sm_installation_date", e.target.value)}
                                    error={meterErrors[idx]?.sm_installation_date}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: darkMode ? colors.f2 : colors.f3,
                                            },
                                            '&:hover fieldset': {
                                                borderColor: darkMode ? colors.f2 : colors.f3,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: darkMode ? colors.f2 : colors.f3,
                                            },
                                            '& input': {
                                                color: darkMode ? colors.f2 : colors.f3,
                                                '&::-webkit-calendar-picker-indicator': {
                                                    filter: darkMode ? 'invert(1)' : 'none',
                                                },
                                            },
                                        },
                                    }}
                                    InputLabelProps={{
                                        shrink: true,
                                        sx: { color: darkMode ? colors.f4 : colors.f4 },
                                    }}
                                    inputProps={{
                                        style: {
                                            color: darkMode ? colors.f2 : colors.f3,
                                        },
                                    }}
                                />
                                {meterErrors[idx]?.sm_installation_date && (
                                    <FormHelperText error>Required</FormHelperText>
                                )}

                            </Grid>

                            {/* Delete Button */}
                            <Grid item xs={12} sm={0.5}>
                                <TextField
                                    margin="dense"
                                    fullWidth
                                    disabled // Optional: disable input so it's not editable
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="center">
                                                <IconButton
                                                    onClick={() => removeMeter(idx)}
                                                    edge="start"
                                                    color="error"
                                                    sx={{ p: 0.5, pl: "30%" }}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: darkMode ? colors.f2 : colors.f3,
                                            },
                                            '&:hover fieldset': {
                                                borderColor: darkMode ? colors.f2 : colors.f3,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: darkMode ? colors.f2 : colors.f3,
                                            },
                                            backgroundColor: 'transparent',
                                        },
                                    }}
                                    InputLabelProps={{ shrink: false }}
                                />
                            </Grid>


                        </Grid>
                    ))}
                </Box>

                {/*Button*/}
                <Box display="flex" justifyContent="center" mt={"3%"} >
                    <Button
                        onClick={() => {
                            navigate('/PlantList');
                        }}
                        sx={{ mr: 2 }}
                    >
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleFormSubmit} disabled={loading} sx={{ backgroundColor: darkMode ? colors.d4 : colors.l4, }}>
                        {loading ? 'Submitting...' : 'Submit'}
                    </Button>
                </Box>
            </Box>
        </Box >
    );
};

export default AddSite;